/**
 * Hook that fetches, sorts, and maps auth-status comments into chat
 * bubble view models for the Query Comments timeline.
 *
 * @module useQueryCommentsData
 */

import { useMemo } from "react";

import { AuthStatusChangeComments } from "../../../../../data-model/authStatusChangeComments";
import { useAuthStatusCommentsRead } from "../../../../../hooks/singleMedicalPaOrderDetails/useAuthStatusCommentsRead";
import { formatDateToUTCWithFormat } from "../../../../../utils/formatDate";
import { ChatMessageItem } from "../queryComments.types";
import {
  AuthStatusCommentType,
  COMMENT_TYPE_TO_TAG_BACKGROUND_COLOR,
  COMMENT_TYPE_TO_TAG_FORE_GROUND_COLOR,
  COMMENT_TYPE_TO_TAG_LABEL,
  DEFAULT_TAG_BACKGROUND_COLOR,
  DEFAULT_TAG_FORE_GROUND_COLOR,
  DEFAULT_TAG_LABEL,
} from "../utils/commentTypeConfig";
import { createNameAndInitialFromEmail } from "../utils/createNameAndInitialFromEmail";

/** Parameters for the {@link useQueryCommentsData} hook. */
interface UseQueryCommentsDataProps {
  /** PA order ID to load comments for. */
  orderId: string;
  /** Organisation ID scoping the request. */
  orgId: string;
}

/** Return shape of {@link useQueryCommentsData}. */
interface UseQueryCommentsDataResult {
  /** Chat-bubble view models in ascending chronological order. */
  chatMessages: ChatMessageItem[];
  /** Whether the underlying fetch is still in flight. */
  loading: boolean;
  /** Fetch error, if any. */
  error: unknown;
  /** True when the fetch was skipped because orderId or orgId is empty. */
  skipped: boolean;
}

/**
 * Maps a single API comment into a {@link ChatMessageItem}.
 *
 * Falls back to neutral grey styling and a generic "Comment" label when
 * `comment_type` is not one of the recognised values.
 *
 * @param item - One raw auth-status-change comment from the API
 * @returns A chat-bubble view model
 */
const toChatMessageItem = (item: AuthStatusChangeComments): ChatMessageItem => {
  const { name, initial } = createNameAndInitialFromEmail(item.commented_by);
  const commentType = item.comment_type as AuthStatusCommentType;
  const isKnownType = commentType in COMMENT_TYPE_TO_TAG_LABEL;

  return {
    avatarLabel: initial,
    message: `${item.j_code ? "(" + item.j_code + ") " : ""}${item.reason} : ${item.comment}`,
    senderName: name,
    timestamp: formatDateToUTCWithFormat(
      item.created_at,
      "ddd, MM/DD/YYYY h:mm A",
    ),
    tagLabel: isKnownType
      ? COMMENT_TYPE_TO_TAG_LABEL[commentType]
      : DEFAULT_TAG_LABEL,
    tagForeGroundColor: isKnownType
      ? COMMENT_TYPE_TO_TAG_FORE_GROUND_COLOR[commentType]
      : DEFAULT_TAG_FORE_GROUND_COLOR,
    tagBackgroundColor: isKnownType
      ? COMMENT_TYPE_TO_TAG_BACKGROUND_COLOR[commentType]
      : DEFAULT_TAG_BACKGROUND_COLOR,
  };
};

/**
 * Fetches auth-status comments for the given order and returns them as
 * an ascending-chronological list of chat-bubble view models.
 *
 * @param props - Order ID and org ID identifying the comments to load
 * @returns chatMessages, loading, and error
 */
export const useQueryCommentsData = (
  props: UseQueryCommentsDataProps,
): UseQueryCommentsDataResult => {
  const { orderId, orgId } = props;
  const { data, loading, error, skipped } = useAuthStatusCommentsRead({
    orderId,
    orgId,
  });

  const chatMessages = useMemo<ChatMessageItem[]>(() => {
    if (!data?.comments) {
      return [];
    }
    const sorted = [...data.comments].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
    );
    return sorted.map(toChatMessageItem);
  }, [data]);

  return { chatMessages, loading, error, skipped };
};
